// Library data for the web app: pages, facets, favourites, collections, and the words rows show.
import {
  CONSTRAINT_NAMES,
  KIND_NAMES,
  filterParams,
  gridKinds,
  type CollectionDto,
  type CollectionRequest,
  type CollectionsResponse,
  type FavouriteResponse,
  type GridKind,
  type LibraryFacets,
  type LibraryFilters,
  type LibraryItem,
  type LibraryPage,
  type PuzzleAbout,
  type PuzzleSummary,
} from '@zinc/shared';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { api } from './api';

/** Short source names, as on the mockups ("CtC · LMD · BremSter"). */
export const SOURCE_SHORT: Record<string, string> = {
  ctc: 'CtC',
  lmd: 'LMD',
  bremster: 'BremSter',
  artisanal: 'Artisanal',
  gmpuzzles: 'GMPuzzles',
  import: 'Imported',
  generator: 'Generator',
  builder: 'Built here',
};

export function sourceName(key: string, full?: string): string {
  return SOURCE_SHORT[key] ?? full ?? key;
}

/** A constraint type or grid kind as a word. Kropki's two dots get their colour in menus. */
export function typeName(type: string, forMenu = false): string {
  if (forMenu && type === 'difference') return 'Kropki, white dots';
  if (forMenu && type === 'ratio') return 'Kropki, black dots';
  if (type in KIND_NAMES) return KIND_NAMES[type as GridKind];
  return (CONSTRAINT_NAMES as Record<string, string>)[type] ?? type;
}

/** The row's tags (mockup 06): up to two rule names, the source, hand-set or generated. */
export function tagsOf(p: PuzzleSummary & { preview: LibraryItem['preview'] }): string {
  const known = p.constraintTypes.filter((t) => t in CONSTRAINT_NAMES);
  const names = [
    ...new Set([
      ...gridKinds(p.preview, known).map((k) => KIND_NAMES[k]),
      ...known.map((t) => typeName(t)),
    ]),
  ];
  // Needs-a-plugin puzzles without a known rule show no "Classic": their rule isn't classic.
  const shown = p.status === 'needs-plugin' ? names.filter((n) => n !== 'Classic') : names;
  const types = shown.length > 2 ? [...shown.slice(0, 2), `+${shown.length - 2}`] : shown;
  return [...types, sourceName(p.source), p.origin].join(' · ');
}

/** The search string for filters, '' when there are none. */
export function filterQuery(f: LibraryFilters): string {
  return new URLSearchParams(filterParams(f)).toString();
}

const PAGE = 40;

export const libraryKeys = {
  all: ['library'] as const,
  list: (f: LibraryFilters) => ['library', 'list', filterQuery(f)] as const,
  facets: ['library', 'facets'] as const,
  collections: ['collections'] as const,
  home: ['home'] as const,
};

export function useLibrary(filters: LibraryFilters) {
  return useInfiniteQuery({
    queryKey: libraryKeys.list(filters),
    queryFn: ({ pageParam }) => {
      const q = new URLSearchParams(filterParams(filters));
      q.set('limit', String(PAGE));
      if (pageParam) q.set('offset', String(pageParam));
      return api<LibraryPage>(`/api/puzzles?${q.toString()}`);
    },
    initialPageParam: 0,
    getNextPageParam: (last) => last.nextOffset ?? undefined,
  });
}

export function useFacets() {
  return useQuery({
    queryKey: libraryKeys.facets,
    queryFn: () => api<LibraryFacets>('/api/library/facets'),
    staleTime: 5 * 60_000,
  });
}

export function useCollections() {
  return useQuery({
    queryKey: libraryKeys.collections,
    queryFn: () => api<CollectionsResponse>('/api/collections'),
  });
}

/** Star or unstar; every cached list shows it at once, and goes back if saving fails. */
export function useFavourite() {
  const client = useQueryClient();
  const patch = (id: string, favourite: boolean) => {
    client.setQueryData<PuzzleAbout>(['puzzle-about', id], (about) =>
      about ? { ...about, favourite } : about,
    );
    client.setQueriesData<InfiniteData<LibraryPage>>({ queryKey: ['library', 'list'] }, (data) =>
      data
        ? {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              puzzles: page.puzzles.map((p) => (p.id === id ? { ...p, favourite } : p)),
            })),
          }
        : data,
    );
  };
  return useMutation({
    mutationFn: ({ id, favourite }: { id: string; favourite: boolean }) =>
      api<FavouriteResponse>(`/api/puzzles/${id}/favourite`, {
        method: 'PUT',
        json: { favourite },
      }),
    onMutate: ({ id, favourite }) => patch(id, favourite),
    onError: (_err, { id, favourite }) => patch(id, !favourite),
    onSettled: (_data, _err, { id }) => {
      void client.invalidateQueries({ queryKey: ['puzzle-about', id] });
      // A favourites filter may now include or leave out the puzzle.
      void client.invalidateQueries({ queryKey: ['library', 'list'], refetchType: 'none' });
    },
  });
}

export function useSaveCollection() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: CollectionRequest & { id?: string }) =>
      id
        ? api<CollectionDto>(`/api/collections/${id}`, { method: 'PATCH', json: body })
        : api<CollectionDto>('/api/collections', { method: 'POST', json: body }),
    onSuccess: () => client.invalidateQueries({ queryKey: libraryKeys.collections }),
  });
}

export function useDeleteCollection() {
  const client = useQueryClient();
  return useMutation({
    // 204 has no body: api() reads it as null.
    mutationFn: (id: string) => api<null>(`/api/collections/${id}`, { method: 'DELETE' }),
    onSuccess: () => client.invalidateQueries({ queryKey: libraryKeys.collections }),
  });
}

export function useCollectionMembership() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      collectionId,
      puzzleId,
      member,
    }: {
      collectionId: string;
      puzzleId: string;
      member: boolean;
    }) =>
      api<CollectionDto>(`/api/collections/${collectionId}/puzzles`, {
        method: 'POST',
        json: { puzzleId, member },
      }),
    onSettled: (_d, _e, { puzzleId }) => {
      void client.invalidateQueries({ queryKey: libraryKeys.collections });
      void client.invalidateQueries({ queryKey: ['puzzle-about', puzzleId] });
      void client.invalidateQueries({ queryKey: ['library', 'list'], refetchType: 'none' });
    },
  });
}
